import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Card,
  CardContent,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import MultiSelectField from "../../atoms/MultiSelectField/MultiSelectField";
import SelectField from "../../atoms/SelectField/SelectField";
import {
  fetchPolos,
  createPolo,
  updatePolo,
  deletePolo,
} from "../../../API/apiPoloService";
import getCidades from "../../../API/getCidades";
import { Polo } from "../../../types/acao";
import styles from "./PolosConfigPage.module.css";
import FileUploadComponet from "../../atoms/FileUpload/FileUpload";

async function readFileWithEncoding(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      if (!event.target?.result) {
        reject("Erro ao ler o arquivo.");
        return;
      }

      const arrayBuffer = event.target.result as ArrayBuffer;
      const uint8Array = new Uint8Array(arrayBuffer);

      // Lista de codificações para testar
      const encodings = [
        "utf-8",
        "iso-8859-1",
        "windows-1252",
        "utf-16le",
        "utf-16be",
        "shift_jis",
        "euc-jp",
        "big5",
      ];

      for (const encoding of encodings) {
        try {
          const text = new TextDecoder(encoding, { fatal: false }).decode(
            uint8Array
          );
          if (!text.includes("")) {
            resolve(text); // Retorna o texto decodificado
            return;
          }
        } catch (error) {
          console.warn(`Erro ao tentar decodificar com ${encoding}:`, error);
        }
      }

      reject("Não foi possível detectar a codificação corretamente.");
    };

    reader.readAsArrayBuffer(file);
  });
}

const PolosConfigPage: React.FC = () => {
  const [polos, setPolos] = useState<Polo[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [cidades, setCidades] = useState<{ nome: string }[]>([]);
  const [novoPolo, setNovoPolo] = useState({
    nome: "",
    cidadeSede: "",
    cidades: [] as string[],
  });
  const [editando, setEditando] = useState<boolean>(false);
  const [poloEditando, setPoloEditando] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [cidadesDialog, setCidadesDialog] = useState<string[]>([]);

  useEffect(() => {
    carregarDados();
    carregarCidades();
  }, []);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const polosData = await fetchPolos();
      if (Array.isArray(polosData)) {
        // Ordenando os polos por nome antes de atualizar o estado
        const polosOrdenados = polosData.sort((a, b) =>
          a.nome.localeCompare(b.nome)
        );
        setPolos(polosOrdenados);
      } else {
        console.error("Erro: A API não retornou um array válido para polos.");
      }
    } catch (error) {
      console.error("Erro ao carregar polos:", error);
    } finally {
      setLoading(false);
    }
  };

  const carregarCidades = async () => {
    try {
      const cidadesData = await getCidades();
      if (Array.isArray(cidadesData)) {
        setCidades(cidadesData);
      } else {
        console.error("Erro: A API não retornou um array válido para cidades.");
      }
    } catch (error) {
      console.error("Erro ao carregar cidades:", error);
    }
  };

  // Função para remover acentos, caracteres especiais e converter para minúsculas
  const normalizeString = (str: string) =>
    str
      .normalize("NFD") // Separa caracteres acentuados
      .replace(/[\u0300-\u036f]/g, "") // Remove acentos
      .replace(/ç/g, "c") // Transforma 'ç' em 'c'
      .replace(/[-_"]/g, " ") // Remove traços, underlines e apóstrofos, substituindo por espaço
      .replace(/[']/g, "")
      .replace(/\s+/g, " ") // Remove múltiplos espaços consecutivos
      .replace(/[^a-zA-Z\s]/g, "") // Mantém apenas letras e espaços
      .toLowerCase()
      .trim();

  const cidadesMap = new Map(
    cidades.map((c) => [normalizeString(c.nome), c.nome])
  );

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const decodedText = await readFileWithEncoding(file);

      // Processar cidades do arquivo e atualizar o estado
      const cidadesFromFile = decodedText
        .split(/\r?\n/) // Divide em linhas
        .map((line) => normalizeString(line)) // Normaliza cada cidade do arquivo
        .filter((line) => cidadesMap.has(line)) // Mantém apenas cidades existentes no vetor cidades
        .map(
          (cidadeNormalizada) => cidadesMap.get(cidadeNormalizada) as string
        );

      setNovoPolo({ ...novoPolo, cidades: cidadesFromFile });
    } catch (error) {
      console.error(error);
    }
  };

  const handleCriarOuEditarPolo = async () => {
    if (!novoPolo.nome || !novoPolo.cidadeSede || novoPolo.cidades.length === 0)
      return;
    try {
      if (editando && poloEditando !== null) {
        // Atualizar Polo existente
        await updatePolo(
          poloEditando,
          novoPolo.nome,
          novoPolo.cidadeSede,
          novoPolo.cidades
        );
      } else {
        // Criar novo Polo
        await createPolo(novoPolo.nome, novoPolo.cidadeSede, novoPolo.cidades);
      }
      setNovoPolo({ nome: "", cidadeSede: "", cidades: [] });
      setEditando(false);
      setPoloEditando(null);
      carregarDados();
    } catch (error) {
      console.error("Erro ao salvar polo:", error);
    }
  };

  const handleEditarPolo = (polo: Polo) => {
    setNovoPolo({
      nome: polo.nome,
      cidadeSede: polo.cidade_sede,
      cidades: polo.cidades ?? [],
    });
    setEditando(true);
    setPoloEditando(polo.id);
  };

  const handleExcluirPolo = async (id: number) => {
    try {
      await deletePolo(id);
      carregarDados();
    } catch (error) {
      console.error("Erro ao excluir polo:", error);
    }
  };

  const handleAbrirDialogoCidades = (cidades: string[]) => {
    setCidadesDialog(cidades);
    setDialogOpen(true);
  };

  return (
    <Card className={styles.card}>
      <CardContent>
        <Typography variant="h4" className={styles.title}>
          Configuração de Polos
        </Typography>

        <div className={styles.formContainer}>
          <TextField
            label="Nome do Polo"
            value={novoPolo.nome}
            onChange={(e) => setNovoPolo({ ...novoPolo, nome: e.target.value })}
            className={styles.input}
            fullWidth
          />

          <SelectField
            label="Cidade Sede"
            value={novoPolo.cidadeSede}
            onChange={(value) =>
              setNovoPolo({ ...novoPolo, cidadeSede: value })
            }
            options={cidades}
            required
            fullWidth
          />

          <MultiSelectField
            label="Cidades Associadas"
            value={novoPolo.cidades}
            onChange={(value) => {
              setNovoPolo({ ...novoPolo, cidades: value as string[] });
            }}
            options={cidades}
            required
            fullWidth
          />

          <FileUploadComponet onFileSelect={handleFileUpload} />

          <Button
            variant="contained"
            color="primary"
            onClick={handleCriarOuEditarPolo}
            className={styles.createButton}
          >
            {editando ? "Atualizar Polo" : "Criar Polo"}
          </Button>
        </div>

        {loading ? (
          <CircularProgress className={styles.loading} />
        ) : (
          <TableContainer component={Paper} className={styles.tableContainer}>
            <Table>
              <TableHead>
                <TableRow className={styles.tableHeader}>
                  <TableCell>Nome</TableCell>
                  <TableCell>Cidade Sede</TableCell>
                  <TableCell>Cidades Associadas</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {polos.map((polo) => (
                  <TableRow key={polo.id}>
                    <TableCell>{polo.nome}</TableCell>
                    <TableCell>{polo.cidade_sede}</TableCell>
                    <TableCell>
                      <button
                        className="buttonSecondary"
                        onClick={() => handleAbrirDialogoCidades(polo.cidades)}
                      >
                        Ver Todas
                      </button>
                    </TableCell>
                    <TableCell>
                      <button
                        className="buttonPrimary"
                        onClick={() => handleEditarPolo(polo)}
                      >
                        Editar
                      </button>
                      <button
                        className="buttonDanger"
                        onClick={() => handleExcluirPolo(polo.id)}
                      >
                        Excluir
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Caixa de Diálogo para Visualizar Cidades Associadas */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <DialogTitle>Cidades Associadas</DialogTitle>
          <DialogContent>
            <ul>
              {cidadesDialog.map((cidade, index) => (
                <li key={index}>{cidade}</li>
              ))}
            </ul>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default PolosConfigPage;
